import { PROMO_PLANS, type AdminPromoCampaignView } from '@steado/admin-contract/promotions';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactNode } from 'react';

import { Labelled, MICRO_LABEL, SelectField } from '@/components/form/fields';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth/auth-context';

import {
  EMPTY_PROMOTION_FORM,
  formIssues,
  formValuesOf,
  PLAY_BASE_PLAN_SUGGESTIONS,
  toCreateBody,
  toUpdateBody,
  type PromotionFormValues,
} from './promotion-form';
import { createPromotion, PROMOTIONS_KEY, updatePromotion } from './promotions-query';

const APPLE_HELP =
  'Create the offer code in App Store Connect first. The reference name must match exactly — purchases are credited by it. The custom code is what users type at Apple, usually the same as the campaign code.';

const PLAY_HELP =
  'Create an offer on the base plan in Play Console with eligibility "Developer determined" and add the tag rc-ignore-offer, so RevenueCat never offers it to everyone.';

interface PromotionFormDialogProps {
  /** Null composes a new campaign. */
  readonly campaign: AdminPromoCampaignView | null;
  readonly onClose: () => void;
  readonly onSaved?: (campaign: AdminPromoCampaignView) => void;
}

/**
 * Registers a campaign against offers that already exist in the stores. The
 * form cannot create a discount; it can only point at one, so the store
 * sections say where the offer has to be made first.
 */
export function PromotionFormDialog({ campaign, onClose, onSaved }: PromotionFormDialogProps) {
  const { api } = useAuth();
  const queryClient = useQueryClient();
  const isNew = campaign === null;
  const [initial] = useState<PromotionFormValues>(() =>
    campaign === null ? EMPTY_PROMOTION_FORM : formValuesOf(campaign),
  );
  const [values, setValues] = useState<PromotionFormValues>(initial);
  const planListId = useId();

  const save = useMutation({
    mutationFn: () =>
      campaign === null
        ? createPromotion(api, toCreateBody(values))
        : updatePromotion(api, campaign.id, toUpdateBody(values, initial)),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: PROMOTIONS_KEY });
      onSaved?.(saved);
      onClose();
    },
    retry: false,
  });

  const issues = formIssues(values, isNew);
  const set = (patch: Partial<PromotionFormValues>): void => {
    setValues((current) => ({ ...current, ...patch }));
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        aria-label={isNew ? 'New campaign' : `Edit ${campaign.code}`}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl"
      >
        <DialogHeader>
          <DialogTitle>{isNew ? 'New campaign' : `Edit ${campaign.code}`}</DialogTitle>
          <DialogDescription>
            The discount itself lives in App Store Connect and Play Console. This ties a code users type to
            those offers and counts what it brings in.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <div className="grid gap-4 sm:grid-cols-2">
            {isNew ? (
              <TextField
                label="Code"
                value={values.code}
                placeholder="STEADO50"
                hint="What users type in the app. 4 to 32 letters and digits."
                mono
                onChange={(code) => {
                  set({ code: code.toUpperCase() });
                }}
              />
            ) : null}
            <TextField
              label="Name"
              value={values.name}
              placeholder="Diwali 2026"
              onChange={(name) => {
                set({ name });
              }}
            />
            <TextField
              label="Discount label"
              value={values.discountLabel}
              placeholder="50% off your first year"
              hint="Shown to users, e.g. 50% off your first year"
              onChange={(discountLabel) => {
                set({ discountLabel });
              }}
            />
            <SelectField
              label="Applies to"
              options={PROMO_PLANS}
              value={values.appliesTo}
              disabled={false}
              onChange={(appliesTo) => {
                set({ appliesTo });
              }}
            />
          </div>

          <StoreSection title="App Store" help={APPLE_HELP}>
            <TextField
              label="Offer reference name"
              value={values.appleOfferReference}
              mono
              onChange={(appleOfferReference) => {
                set({ appleOfferReference });
              }}
            />
            <TextField
              label="Custom code"
              value={values.appleCustomCode}
              mono
              onChange={(appleCustomCode) => {
                set({ appleCustomCode });
              }}
            />
          </StoreSection>

          <StoreSection title="Google Play" help={PLAY_HELP}>
            <TextField
              label="Base plan id"
              value={values.playBasePlanId}
              placeholder={PLAY_BASE_PLAN_SUGGESTIONS[0]}
              list={planListId}
              mono
              onChange={(playBasePlanId) => {
                set({ playBasePlanId });
              }}
            />
            <datalist id={planListId}>
              {PLAY_BASE_PLAN_SUGGESTIONS.map((plan) => (
                <option key={plan} value={plan} />
              ))}
            </datalist>
            <TextField
              label="Offer id"
              value={values.playOfferId}
              mono
              onChange={(playOfferId) => {
                set({ playOfferId });
              }}
            />
          </StoreSection>

          <div className="grid gap-4 sm:grid-cols-2">
            <DateField
              label="Starts at"
              value={values.startsAt}
              placeholder="Right away"
              onChange={(startsAt) => {
                set({ startsAt });
              }}
            />
            <DateField
              label="Ends at"
              value={values.endsAt}
              placeholder="No end date"
              onChange={(endsAt) => {
                set({ endsAt });
              }}
            />
            <TextField
              label="Max claims"
              value={values.maxClaims}
              placeholder="No limit"
              inputMode="numeric"
              onChange={(maxClaims) => {
                set({ maxClaims });
              }}
            />
            <SwitchField
              label="New subscribers only"
              checked={values.newSubscribersOnly}
              onChange={(newSubscribersOnly) => {
                set({ newSubscribersOnly });
              }}
            />
          </div>

          {issues.length === 0 ? null : (
            <ul className="flex list-disc flex-col gap-1 rounded-md border border-border bg-muted py-3 pr-4 pl-8 text-[13px] text-muted-foreground">
              {issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          )}

          {save.isError ? (
            <Alert role="alert" tone="err">
              <AlertDescription>{save.error.message}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={issues.length > 0 || save.isPending}
            onClick={() => {
              save.mutate();
            }}
          >
            {save.isPending ? 'Saving…' : isNew ? 'Create campaign' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StoreSection({
  title,
  help,
  children,
}: {
  readonly title: string;
  readonly help: string;
  readonly children: ReactNode;
}) {
  return (
    <fieldset className="flex flex-col gap-3 rounded-lg border border-border px-4 pt-2 pb-4">
      <legend className={`${MICRO_LABEL} px-1`}>{title}</legend>
      <p className="text-[12.5px] leading-relaxed text-pretty text-muted-foreground">{help}</p>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly hint?: string;
  readonly mono?: boolean;
  readonly list?: string;
  readonly inputMode?: 'numeric';
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  mono = false,
  list,
  inputMode,
}: TextFieldProps) {
  const id = useId();
  return (
    <Labelled id={id} label={label} {...(hint === undefined ? {} : { hint })}>
      <Input
        id={id}
        value={value}
        className={mono ? 'font-mono' : undefined}
        {...(placeholder === undefined ? {} : { placeholder })}
        {...(list === undefined ? {} : { list })}
        {...(inputMode === undefined ? {} : { inputMode })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
    </Labelled>
  );
}

function DateField({
  label,
  value,
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly placeholder: string;
  readonly onChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <Labelled id={id} label={label}>
      <DateTimePicker id={id} value={value} onChange={onChange} placeholder={placeholder} />
    </Labelled>
  );
}

function SwitchField({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id} className={MICRO_LABEL}>
        {label}
      </Label>
      <div className="flex h-9 items-center">
        <Switch id={id} checked={checked} onCheckedChange={onChange} />
      </div>
    </div>
  );
}
